import { useEffect } from "react";
import { Alert, Typography, Button } from "antd";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import {
  CalendarOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import usePlanStore from "../../store/usePlanStore";
import SubscriptionSkeleton from "../../components/Skeleton/SubscriptionSkeleton";
import Footer from "../../components/Footer/Footer";
import "./Subscription.css";

const { Title } = Typography;

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not set" : date.toLocaleDateString();
};

const titleCase = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "Unknown";

export default function Subscription() {
  const navigate = useNavigate();
  const { planData, planUsage, planLoading, fetchUserPlan, planError } =
    usePlanStore();

  useEffect(() => {
    if (!planData) {
      fetchUserPlan();
    }
  }, [planData]);

  const renderDetails = () => {
    const isFreeTrial = planData.paymentIntentId === "free_trial";
    const isCancelled = Boolean(planData.isCancelled);
    const isActive = planData.status === "succeeded";
    const usedSeats = planUsage?.totalUsers;
    const usedProducts = planUsage?.totalProducts;

    const details = [
      {
        key: "duration",
        Icon: ClockCircleOutlined,
        label: "Billing period",
        value: `${planData.planDuration ?? "-"} month${
          planData.planDuration === 1 ? "" : "s"
        }`,
      },
      {
        key: "expiry",
        Icon: CalendarOutlined,
        // Wording follows state: an active plan renews, a cancelled one
        // simply runs out.
        label: isCancelled ? "Access ends" : "Renews on",
        value: formatDate(planData.expiry),
      },
      {
        key: "people",
        Icon: UserOutlined,
        label: "Team seats",
        /* Usage against the limit, not the limit alone. Both numbers come
           from the same /userPlan call, so a bare "10" was hiding a figure
           already in hand. */
        value:
          typeof usedSeats === "number" && typeof planData.numOfPeople === "number"
            ? `${usedSeats} of ${planData.numOfPeople} used`
            : planData.numOfPeople ?? "-",
      },
      {
        key: "products",
        Icon: ShoppingCartOutlined,
        label: "Products",
        value: (() => {
          const limit = planData.numOfProduct;
          if (typeof usedProducts !== "number") {
            return limit === -1 ? "Unlimited" : limit ?? "-";
          }
          return limit === -1
            ? `${usedProducts} of unlimited`
            : `${usedProducts} of ${limit} used`;
        })(),
      },
    ];

    return (
      <section className="sub-card">
        <header className="sub-card-head">
          {/* The amount is the headline fact, so it leads rather than sitting
              as one of six equal-weight cells in the grid below. */}
          <div className="sub-amount">
            {isFreeTrial ? (
              <span className="sub-amount-value">Free trial</span>
            ) : (
              <>
                <span className="sub-amount-value">
                  ${planData.amount ?? "0"}
                </span>
                <span className="sub-amount-meta">
                  {(planData.currency || "usd").toUpperCase()}
                </span>
              </>
            )}
            {/* Sits with the amount rather than as a fifth cell in the grid
                below, which would leave a hole in the two-column layout. */}
            {planData.createdDate && (
              <span className="sub-amount-since">
                Started {formatDate(planData.createdDate)}
              </span>
            )}
          </div>
          <span
            className={`sub-status ${
              isCancelled
                ? "sub-status-cancelled"
                : isActive
                ? "sub-status-active"
                : "sub-status-attention"
            }`}
          >
            {isCancelled ? "Cancelled" : titleCase(planData.status)}
          </span>
        </header>

        {isCancelled && (
          <Alert
            type="warning"
            showIcon
            className="sub-alert"
            message="This subscription is cancelled"
            description={`You keep access until ${formatDate(
              planData.expiry
            )}. Choose a plan to stay active after that.`}
          />
        )}

        <dl className="sub-grid">
          {details.map(({ key, Icon, label, value }) => (
            <div className="sub-item" key={key}>
              <Icon className="sub-item-icon" aria-hidden="true" />
              <div className="sub-item-body">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            </div>
          ))}
        </dl>

        <footer className="sub-card-foot">
          {/* Stripe writes receipt_url onto the payment; the page never
              surfaced it, so there was no way to get a receipt at all. Only
              rendered when Stripe actually returned one. */}
          {planData.receipt_url && (
            <a
              className="sub-receipt-link"
              href={planData.receipt_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View receipt
            </a>
          )}
          {/* A cancelled plan still needs a way back, which the previous
              version hid entirely. */}
          <Button
            type="primary"
            className="sub-primary-btn"
            onClick={() => navigate("/plans")}
          >
            {isCancelled ? "Choose a plan" : "Manage subscription"}
          </Button>
        </footer>
      </section>
    );
  };

  return (
    <>
      <Navbar />
      <div className="subscription-page">
        <header className="subscription-head">
          <Title level={2} className="subscription-title">
            Your subscription
          </Title>
          <p className="subscription-sub">
            Your plan, what it includes, and how much of it you are using.
          </p>
        </header>

        {planLoading ? (
          <SubscriptionSkeleton />
        ) : planError ? (
          <Alert message={planError} type="error" showIcon />
        ) : planData ? (
          renderDetails()
        ) : (
          <section className="sub-card sub-empty">
            <p className="sub-empty-title">No active subscription</p>
            <p className="sub-empty-text">
              Choose a plan to unlock team seats and product uploads.
            </p>
            <Button
              type="primary"
              className="sub-primary-btn"
              onClick={() => navigate("/plans")}
            >
              View plans
            </Button>
          </section>
        )}
      </div>
      <Footer />
    </>
  );
}
